// Service API pour le Kanban (appels sécurisés côté serveur)

import type { Task } from '../pages/AdminKanban';

const API_BASE = '/api/kanban/tasks';

// Récupérer le token d'authentification
function getAuthToken(): string | null {
  return localStorage.getItem('admin_token');
}

// Headers par défaut
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
}

// Liste toutes les tâches
export async function getTasks(): Promise<Task[]> {
  const response = await fetch(API_BASE, {
    headers: getHeaders(),
  });
  
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    if (response.status === 503) {
      throw new Error('Firebase not configured');
    }
    throw new Error('Failed to fetch tasks');
  }
  
  const data = await response.json();
  // Support both new (array) and potentially old (object with tasks) formats just in case
  return Array.isArray(data) ? data : (data.tasks || []);
}

// Créer une tâche
export async function createTask(task: Omit<Task, 'id'>): Promise<Task> {
  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(task),
  });
  
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    throw new Error('Failed to create task');
  }
  
  return response.json();
}

// Mettre à jour une tâche
export async function updateTask(taskId: string, updates: Partial<Task>): Promise<Task> {
  const response = await fetch(`${API_BASE}/${taskId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(updates),
  });
  
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    if (response.status === 404) {
      throw new Error('Task not found');
    }
    throw new Error('Failed to update task');
  }
  
  return response.json();
}

// Supprimer une tâche
export async function deleteTask(taskId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/${taskId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    throw new Error('Failed to delete task');
  }
}
